/** Rules and limits: time rules per label, concurrency limits, headroom and the offline policy per project. */
import { Clock, Plus, Trash2 } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { OFFLINE_POLICY_LABEL, agentKindLabel, describeRule, offeredAgentKinds, ruleActive, timeRuleError } from '@orgus/protocol';
import type { Id, Machine, NodeCapacity, OfflinePolicy, Project, SchedulerState } from '@orgus/protocol';
import { Button, Dialog, EmptyState, ErrorState, IconButton, Input, Panel, ProjectMark, REVEAL_GROUP, RevealSlot, RowStatus, cn, toast, tableClass } from '@orgus/ui';
import { InlineChoice, InlineText } from '../detail/InlineEdit.tsx';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { AgentSwitches } from './AgentSwitches.tsx';
import { addRule, deleteRule, patchSettings, putProject } from './data.ts';
import { WEEK, offsetLabel } from './model.ts';

function RuleDialog({ open, onOpenChange, knownLabels }: { open: boolean; onOpenChange: (v: boolean) => void; knownLabels: string[] }) {
  const [label, setLabel] = useState('laptop');
  const [from, setFrom] = useState('22:00');
  const [to, setTo] = useState('07:00');
  const [days, setDays] = useState<number[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => {
    if (!open) return;
    setLabel(knownLabels.includes('laptop') ? 'laptop' : knownLabels[0] ?? 'laptop');
    setFrom('22:00');
    setTo('07:00');
    setDays([]);
    setNote('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const body = { label: label.trim().toLowerCase(), from, to: to === '00:00' && from !== '00:00' ? '24:00' : to, days, note };
  const invalid = timeRuleError(body);
  const submit = async () => {
    if (invalid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await addRule(body);
      toast.success('Rule added', describeRule(r));
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add time rule"
      description="Machines with this label take no new work inside the window."
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!!invalid} onClick={() => void submit()}>
            Add rule
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Machine label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          suggestions={knownLabels}
          className="font-mono"
          hint={knownLabels.length ? undefined : 'Label machines on Machines or Capacity first.'}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="From" type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input label="To" type="time" value={to} onChange={(e) => setTo(e.target.value)} hint={to <= from ? 'Runs past midnight.' : undefined} />
        </div>
        <fieldset className="flex flex-col gap-2">
          {/* The same label as every other field in the form. */}
          <legend className="mb-2 text-ui font-medium text-fg-muted">Days the window starts on</legend>
          {/* Seven equal toggles: the week stays on one row at phone width too. */}
          <div className="grid grid-cols-7 gap-1 sm:flex sm:flex-wrap">
            {WEEK.map((d) => {
              const on = days.includes(d.day);
              return (
                <button
                  key={d.day}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setDays((xs) => (on ? xs.filter((x) => x !== d.day) : [...xs, d.day]))}
                  className={cn('h-7 min-w-0 rounded-control border px-1 text-caption transition-colors sm:px-3', on ? 'border-accent bg-accent text-accent-fg' : 'border-border text-fg-muted hover:bg-hover')}
                >
                  {d.short}
                </button>
              );
            })}
          </div>
          <span className="text-caption text-fg-subtle">{days.length ? `${days.length} of 7 days` : 'None picked: every day.'}</span>
        </fieldset>
        <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why, for whoever reads it next" maxLength={200} />
        <p className="text-caption text-fg-muted">{invalid ?? describeRule(body)}</p>
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}

function num(text: string): number | null | 'bad' {
  const t = text.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 && n <= 1000 ? n : 'bad';
}

const LIMIT_ERROR = 'A whole number from 0 to 1000, or empty';
const limitProblem = (t: string) => (num(t) === 'bad' ? LIMIT_ERROR : null);
const OFFLINE_OPTIONS = (['wait', 'reschedule'] as const).map((o) => ({ value: o, label: OFFLINE_POLICY_LABEL[o] }));

/** Save one setting at once, as soon as it changes: no form to submit. */
async function saveSetting(patch: Parameters<typeof patchSettings>[0]) {
  try {
    await patchSettings(patch);
    toast.success('Scheduling settings saved', 'Queued missions are placed again with the new limits.');
  } catch (e) {
    toast.error(e);
    throw e;
  }
}

/** One labelled setting: a tiny label over a value that becomes a control on click. */
function Setting({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-caption text-fg-subtle">{label}</span>
      {/* The value's own padding sits in the gutter, so its text lines up under the label. */}
      <div className="-ml-2 flex min-w-0">{children}</div>
    </div>
  );
}

function LimitsPanel({ state, machines }: { state: SchedulerState; machines: Machine[] }) {
  const s = state.settings;
  const first = s.agents?.preferred[0] ?? '';
  // Agents any machine can start (ADR 0049 decision 5), plus the saved choice if no machine offers it now.
  const agents = useMemo(() => {
    const kinds = new Set(machines.flatMap((m) => offeredAgentKinds(m, m.labels)));
    if (first) kinds.add(first);
    return [{ value: '', label: 'Machine order' }, ...[...kinds].map((k) => ({ value: k, label: agentKindLabel(k) }))];
  }, [machines, first]);
  const offsets = useMemo(() => {
    const out = [{ value: '', label: 'Hub time zone' }];
    for (let m = -720; m <= 840; m += 30) out.push({ value: String(m), label: offsetLabel(m) });
    return out;
  }, []);
  const pct = (lo: number, hi: number) => (t: string) => {
    const n = Number(t);
    return t !== '' && Number.isFinite(n) && n >= lo && n <= hi ? null : `Between ${lo}% and ${hi}%`;
  };
  return (
    <Panel title="Limits and headroom">
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
        <Setting label="Sessions per person">
          <InlineText
            label="Sessions per person"
            value={s.limits.perPerson == null ? '' : String(s.limits.perPerson)}
            empty="No limit"
            hint="Counted against the mission owner"
            numeric
            validate={limitProblem}
            onCommit={(t) => saveSetting({ limits: { perPerson: num(t) as number | null } })}
          />
        </Setting>
        <Setting label="Sessions per project">
          <InlineText
            label="Sessions per project"
            value={s.limits.perProject == null ? '' : String(s.limits.perProject)}
            empty="No limit"
            hint="A project limit below overrides this"
            numeric
            validate={limitProblem}
            onCommit={(t) => saveSetting({ limits: { perProject: num(t) as number | null } })}
          />
        </Setting>
        <Setting label="Time zone for time rules">
          <InlineChoice
            label="Time zone for time rules"
            value={s.utcOffsetMinutes == null ? '' : String(s.utcOffsetMinutes)}
            options={offsets}
            onChange={(v) => saveSetting({ utcOffsetMinutes: v === '' ? null : Number(v) }).catch(() => undefined)}
          />
        </Setting>
        <Setting label="CPU ceiling">
          <InlineText
            label="CPU ceiling in percent"
            value={String(Math.round(s.headroom.maxLoad * 100))}
            display={`${Math.round(s.headroom.maxLoad * 100)}%`}
            empty="Not set"
            hint="A machine busier than this takes no new work"
            numeric
            validate={pct(10, 100)}
            onCommit={(t) => saveSetting({ headroom: { maxLoad: Number(t) / 100 } })}
          />
        </Setting>
        <Setting label="Free memory floor">
          <InlineText
            label="Free memory floor in percent"
            value={String(Math.round(s.headroom.minFreeMemory * 100))}
            display={`${Math.round(s.headroom.minFreeMemory * 100)}%`}
            empty="Not set"
            hint="Less free memory than this: no new work"
            numeric
            validate={pct(0, 90)}
            onCommit={(t) => saveSetting({ headroom: { minFreeMemory: Number(t) / 100 } })}
          />
        </Setting>
        <Setting label="Agent to try first">
          <InlineChoice
            label="Agent to try first"
            value={first}
            options={agents}
            onChange={(v) => saveSetting({ agents: { preferred: v ? [v] : [] } }).catch(() => undefined)}
          />
        </Setting>
        <Setting label="When a pinned machine is offline">
          <InlineChoice
            label="When a pinned machine is offline"
            value={s.defaultOffline}
            options={OFFLINE_OPTIONS}
            onChange={(v) => saveSetting({ defaultOffline: v as OfflinePolicy }).catch(() => undefined)}
          />
        </Setting>
      </div>
    </Panel>
  );
}

function ProjectRow({ project, state }: { project: Project; state: SchedulerState }) {
  const own = state.settings.projects[project.id];
  const save = async (patch: { offline?: OfflinePolicy; maxSessions?: number | null }) => {
    try {
      await putProject(project.id, patch);
      toast.success(`Saved for ${project.name}`);
    } catch (e) {
      toast.error(e);
      throw e;
    }
  };
  const fallback = state.settings.limits.perProject;
  // On a phone each project is a small card: its name, then each setting on its own line with a
  // tiny label, so a policy such as "Run on another machine" reads in full.
  return (
    <div role="row" className={cn(tableClass.row, PROJECT_COLS, 'grid h-auto grid-cols-1 items-center gap-x-3 gap-y-2 px-4 py-3 sm:h-row sm:gap-y-1 sm:py-0')}>
      <span role="cell" className={cn(tableClass.name, 'flex min-w-0 items-center gap-2')}>
        <ProjectMark projectId={project.id} name={project.name} size="xs" />
        <span className="truncate">{project.name}</span>
      </span>
      <span role="cell" className="flex min-w-0 flex-col">
        <span className="text-caption text-fg-subtle sm:hidden" aria-hidden>
          Pinned machine offline
        </span>
        <span className="-ml-2 flex min-w-0">
          <InlineChoice
          label={`When the pinned machine of ${project.name} is offline`}
          value={own?.offline ?? state.settings.defaultOffline}
          options={OFFLINE_OPTIONS}
          onChange={(v) => save({ offline: v as OfflinePolicy }).catch(() => undefined)}
          />
        </span>
      </span>
      <span role="cell" className="flex min-w-0 flex-col">
        <span className="text-caption text-fg-subtle sm:hidden" aria-hidden>
          Sessions at once
        </span>
        <span className="-ml-2 flex min-w-0">
          <InlineText
            label={`Session limit for ${project.name}`}
            value={own?.maxSessions == null ? '' : String(own.maxSessions)}
            empty={fallback == null ? 'No limit' : `${fallback}, the default`}
            numeric
            validate={limitProblem}
            onCommit={(t) => save({ maxSessions: num(t) as number | null })}
          />
        </span>
      </span>
    </div>
  );
}

/** The Projects table's columns from sm up; below, each project stacks as a card. */
const PROJECT_COLS = 'sm:grid-cols-[minmax(0,1fr)_200px_120px]';

export function RulesTab({ state, capacity, machines, projects, now }: { state: SchedulerState; capacity: NodeCapacity[]; machines: Machine[]; projects: Project[]; now: number }) {
  const [adding, setAdding] = useState(false);
  const knownLabels = useMemo(() => [...new Set(capacity.flatMap((c) => c.labels).concat(machines.flatMap((m) => m.labels)))].sort(), [capacity, machines]);
  const rules = state.settings.rules;
  const machinesWith = (label: string) => capacity.filter((c) => c.labels.includes(label)).map((c) => c.name);
  const remove = async (id: Id) => {
    try {
      await deleteRule(id);
      toast.success('Rule deleted');
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <div className="flex flex-col gap-4">
      {/* The time zone is one setting under Limits and headroom: not repeated here. */}
      <Panel
        title="Time rules"
        icon={<Clock />}
        flush
        actions={
          <Button size="sm" variant="secondary" icon={<Plus />} onClick={() => setAdding(true)}>
            Add rule
          </Button>
        }
      >
        {rules.length ? (
          <ul>
            {rules.map((r) => {
              const active = ruleActive(r, now, state.settings.utcOffsetMinutes);
              const on = machinesWith(r.label);
              return (
                // A routine schedule is not news: a neutral icon, in effect or not. On a phone the
                // title wraps and the state moves to the meta line; the state's right edge lines up
                // with the panel's action, the delete control appearing beside it on hover.
                <li key={r.id} className={cn(REVEAL_GROUP, 'flex min-h-row-2line items-center gap-3 px-4 py-2')}>
                  <RowStatus {...(active ? { tone: 'neutral' as const, icon: 'paused' as const, label: 'In effect' } : { tone: 'neutral' as const, icon: 'idle' as const, label: 'Not now' })} hideLabel />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-fg sm:truncate">{describeRule(r)}</p>
                    <p className="line-clamp-2 text-caption text-fg-subtle sm:truncate">
                      <span className="sm:hidden">{active ? 'In effect. ' : 'Not now. '}</span>
                      {on.length ? `Applies to ${on.join(', ')}` : `No machine has the ${r.label} label yet`}
                      {r.note ? `. ${r.note}` : ''}
                    </p>
                  </div>
                  <RevealSlot className="shrink-0">
                    <IconButton label={`Delete rule: ${describeRule(r)}`} icon={<Trash2 />} variant="danger" size="sm" onClick={() => void remove(r.id)} />
                  </RevealSlot>
                  <span className="hidden shrink-0 text-caption text-fg-subtle sm:inline">{active ? 'In effect' : 'Not now'}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState icon={<Clock />} title="No time rules" description="Keep new work off some machines at some hours" action={<Button onClick={() => setAdding(true)}>Add rule</Button>} />
        )}
      </Panel>
      <LimitsPanel state={state} machines={machines} />
      <AgentSwitches state={state} machines={machines} />
      <Panel title="Projects" sub={`${projects.length} ${projects.length === 1 ? 'project' : 'projects'}`} flush>
        {projects.length ? (
          <div role="table" aria-label="Project scheduling">
            <div role="row" className={cn(tableClass.head, PROJECT_COLS, 'hidden items-center gap-3 border-b border-border-subtle px-4 sm:grid')}>
              <span role="columnheader">Project</span>
              <span role="columnheader">Pinned machine offline</span>
              <span role="columnheader">Sessions at once</span>
            </div>
            {projects.map((p) => (
              <ProjectRow key={p.id} project={p} state={state} />
            ))}
          </div>
        ) : (
          <EmptyState title="No projects yet" description="Projects appear when a machine watches a repository." />
        )}
      </Panel>
      <RuleDialog open={adding} onOpenChange={setAdding} knownLabels={knownLabels} />
    </div>
  );
}
